import { useState, useEffect } from 'react';
import {
  FiShield, FiActivity, FiAlertTriangle, FiInfo, FiAlertCircle,
  FiSearch, FiChevronLeft, FiChevronRight,
  FiMapPin, FiUser, FiClock, FiRefreshCw, FiDownload
} from 'react-icons/fi';
import { format } from 'date-fns';
import { auditAPI, type ActivityLogEntry, type ActivityLogResponse } from '../../api/audit-logging';
import { Card } from '../ui/Card/Card';
import { Button } from '../ui/Button/Button';
import { Badge } from '../ui/Badge/Badge';
import Modal from '../Modal/Modal';
import { useToast } from '../../hooks/useToast';
import styles from './ActivityLogTable.module.css';

import { logger } from '../../utils/logger';

interface ActivityLogTableProps {
  organizationId: string;
}

export default function ActivityLogTable({ organizationId }: ActivityLogTableProps) {
  const [data, setData] = useState<ActivityLogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<ActivityLogEntry | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const { addToast } = useToast();

  // Filters
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    if (organizationId) {
      loadActivityLog();
    }
  }, [organizationId, currentPage, severityFilter, categoryFilter]);

  const loadActivityLog = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = (currentPage - 1) * pageSize;
      const response = await auditAPI.getActivityLog(organizationId, {
        limit: pageSize,
        offset,
        severity: severityFilter as any || undefined,
      });
      
      setData(response);
    } catch (err) {
      setError('Failed to load activity log');
      logger.error('Error loading activity log:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const blob = await auditAPI.exportActivityLog(organizationId, {
        severity: severityFilter as any || undefined,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-log-${organizationId}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      addToast('Activity log exported', 'success');
    } catch (err) {
      logger.error('Error exporting activity log:', err);
      addToast('Failed to export activity log', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <FiAlertCircle />;
      case 'high': return <FiAlertTriangle />;
      case 'medium': return <FiAlertTriangle />;
      case 'low': return <FiInfo />;
      default: return <FiInfo />;
    }
  };

  const getSeverityVariant = (severity: string): 'error' | 'warning' | 'info' | 'secondary' => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'secondary';
    }
  };

  const getCategoryIcon = (category: string) => {
    return category === 'security' ? <FiShield /> : <FiActivity />;
  };

  const getCategoryColor = (category: string) => {
    return category === 'security' ? 'var(--color-error)' : 'var(--color-info)';
  };

  const filteredActivities = data?.activities.filter(activity => {
    if (categoryFilter && activity.category !== categoryFilter) return false;
    if (searchTerm && !activity.description.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !activity.event_type.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  }) || [];

  const totalPages = data ? Math.ceil(data.total / pageSize) : 1;

  if (loading && !data) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <span>Loading activity log...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className={styles.errorCard}>
        <FiAlertCircle className={styles.errorIcon} />
        <h3>Failed to Load Activity Log</h3>
        <p>{error}</p>
        <Button variant="primary" onClick={loadActivityLog}>
          Retry
        </Button>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div className={styles.activityLogTable}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <Button
          variant="secondary"
          size="sm"
          onClick={loadActivityLog}
          disabled={loading}
          icon={<FiRefreshCw />}
        >
          Refresh
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleExport}
          loading={isExporting}
          icon={<FiDownload />}
        >
          Export CSV
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ color: 'var(--color-primary)' }}>
            <FiActivity />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{data.stats.total_events}</div>
            <div className={styles.statLabel}>Total Events</div>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ color: 'var(--color-error)' }}>
            <FiShield />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{data.stats.security_events}</div>
            <div className={styles.statLabel}>Security Events</div>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ color: 'var(--color-warning)' }}>
            <FiAlertTriangle />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{data.stats.high_risk_events}</div>
            <div className={styles.statLabel}>High Risk</div>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon} style={{ color: 'var(--color-error)' }}>
            <FiAlertCircle />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{data.stats.suspicious_events}</div>
            <div className={styles.statLabel}>Suspicious</div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className={styles.filtersCard}>
        <div className={styles.filters}>
          <div className={styles.searchBox}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Categories</option>
            <option value="security">Security</option>
            <option value="activity">Activity</option>
          </select>

          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="info">Info</option>
          </select>

          {(searchTerm || categoryFilter || severityFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('');
                setSeverityFilter('');
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Activity Table */}
      <Card className={styles.tableCard}>
        {filteredActivities.length === 0 ? (
          <div className={styles.emptyState}>
            <FiActivity className={styles.emptyIcon} />
            <h3>No activities found</h3>
            <p>Try adjusting your filters or check back later</p>
          </div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Category</th>
                    <th>Event</th>
                    <th>User</th>
                    <th>Severity</th>
                    <th>Location</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivities.map((activity) => (
                    <tr key={activity.id} className={activity.is_suspicious ? styles.suspiciousRow : ''}>
                      <td>
                        <div className={styles.timeCell}>
                          <FiClock className={styles.cellIcon} />
                          {format(new Date(activity.timestamp), 'MMM dd, HH:mm')}
                        </div>
                      </td>
                      <td>
                        <div className={styles.categoryCell} style={{ color: getCategoryColor(activity.category) }}>
                          {getCategoryIcon(activity.category)}
                          <span>{activity.category}</span>
                        </div>
                      </td>
                      <td>
                        <div className={styles.eventCell}>
                          <div className={styles.eventType}>{activity.event_type}</div>
                          <div className={styles.eventDescription}>{activity.description}</div>
                          {activity.is_suspicious && (
                            <Badge variant="error" size="sm">Suspicious</Badge>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={styles.userCell}>
                          <FiUser className={styles.cellIcon} />
                          {activity.user_name || activity.user_id || 'System'}
                        </div>
                      </td>
                      <td>
                        <Badge variant={getSeverityVariant(activity.severity)} size="sm">
                          {getSeverityIcon(activity.severity)}
                          {activity.severity}
                        </Badge>
                      </td>
                      <td>
                        <div className={styles.locationCell}>
                          {activity.ip_address && (
                            <>
                              <FiMapPin className={styles.cellIcon} />
                              {activity.ip_address}
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedActivity(activity)}
                        >
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <FiChevronLeft />
                  Previous
                </Button>

                <div className={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </div>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={!data.has_more}
                >
                  Next
                  <FiChevronRight />
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Activity Details Modal */}
      <Modal
        isOpen={!!selectedActivity}
        onClose={() => setSelectedActivity(null)}
        title="Activity Details"
        size="large"
      >
        {selectedActivity && (
          <div className={styles.activityDetails}>
            <div className={styles.detailSection}>
              <h4>Event Information</h4>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Event Type:</span>
                  <span className={styles.detailValue}>{selectedActivity.event_type}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Category:</span>
                  <Badge variant={selectedActivity.category === 'security' ? 'error' : 'info'}>
                    {selectedActivity.category}
                  </Badge>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Severity:</span>
                  <Badge variant={getSeverityVariant(selectedActivity.severity)}>
                    {selectedActivity.severity}
                  </Badge>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Timestamp:</span>
                  <span className={styles.detailValue}>
                    {format(new Date(selectedActivity.timestamp), 'PPpp')}
                  </span>
                </div>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Description:</span>
                <p className={styles.detailValue}>{selectedActivity.description}</p>
              </div>
            </div>

            {selectedActivity.user_id && (
              <div className={styles.detailSection}>
                <h4>User Information</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>User:</span>
                    <span className={styles.detailValue}>{selectedActivity.user_name || selectedActivity.user_id}</span>
                  </div>
                  {selectedActivity.ip_address && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>IP Address:</span>
                      <span className={styles.detailValue}>{selectedActivity.ip_address}</span>
                    </div>
                  )}
                  {selectedActivity.user_agent && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>User Agent:</span>
                      <span className={styles.detailValue}>{selectedActivity.user_agent}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedActivity.risk_score !== undefined && (
              <div className={styles.detailSection}>
                <h4>Risk Assessment</h4>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Risk Score:</span>
                  <div className={styles.riskScore}>
                    <div className={styles.riskBar}>
                      <div
                        className={styles.riskFill}
                        style={{
                          width: `${selectedActivity.risk_score}%`,
                          backgroundColor: selectedActivity.risk_score > 70 ? 'var(--color-error)' :
                                         selectedActivity.risk_score > 40 ? 'var(--color-warning)' :
                                         'var(--color-success)'
                        }}
                      />
                    </div>
                    <span>{selectedActivity.risk_score}/100</span>
                  </div>
                </div>
                {selectedActivity.is_suspicious && (
                  <Badge variant="error">Flagged as Suspicious</Badge>
                )}
              </div>
            )}

            {selectedActivity.changes && (
              <div className={styles.detailSection}>
                <h4>Changes</h4>
                <div className={styles.changesGrid}>
                  <div className={styles.changeColumn}>
                    <h5>Before</h5>
                    <pre>{JSON.stringify(selectedActivity.changes.old, null, 2)}</pre>
                  </div>
                  <div className={styles.changeColumn}>
                    <h5>After</h5>
                    <pre>{JSON.stringify(selectedActivity.changes.new, null, 2)}</pre>
                  </div>
                </div>
              </div>
            )}

            {selectedActivity.metadata && Object.keys(selectedActivity.metadata).length > 0 && (
              <div className={styles.detailSection}>
                <h4>Additional Metadata</h4>
                <pre className={styles.metadata}>
                  {JSON.stringify(selectedActivity.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

